import { selectFrom, selectSingleFrom } from '../utils/dom';
import BaseController from './BaseController';

export const ELEMENT_CONTROLLER_ID = 'ElementController';
export const ELEMENT_CONTROLLER_ROOT_ATTRIBUTE = 'data-controller';

export const NODE_NAME_ATTRIBUTE = 'data-node-name';

/**
 * Controller bound to an element.
 *
 * Requires a data attribute with the controller name on an element that will
 * then be the controller's root. If a root doesn't exist on the page, the
 * controller will not be initialized.
 */
export default class ElementController extends BaseController {
  /**
   * @see BaseController._baseId
   */
  static _baseId = ELEMENT_CONTROLLER_ID;

  /**
   * The controller root DOM node.
   *
   * @member {?Object}
   */
  _root = null;

  /**
   * Initialize base controller.
   *
   * @param {Function} controllerConstructor - The controller class being
   *   instantiated.
   * @param {?Object} rootElement - The controller's root element.
   */
  _init(controllerConstructor, rootElement) {
    this._root = rootElement;
    this._initBase(controllerConstructor);
  }

  /**
   * Get the controller's root element.
   *
   * @return {?Object} Root element, or null if no root is set.
   */
  getRoot() {
    this._validateRoot();
    return this._root;
  }

  /**
   * Get nodes contained inside the controller root.
   *
   * @param {string} selector - Node selector. Prefer a name set with data
   *   attribute if possible.
   * @param {boolean} asNodeList - Return the live NodeList instead of
   *   converting the result to an array.
   * @return {Array.<HTMLElement> | NodeList} Found element or null.
   */
  getNodes(selector, asNodeList = false) {
    this._validateRoot();
    return selectFrom(
      this._root,
      this._getControllerSelector(selector),
      asNodeList,
    );
  }

  /**
   * Get a single node contained inside the controller root.
   *
   * @param {string} selector - Node selector. Prefer a name set with data
   *   attribute if possible.
   * @return {?HTMLElement} Found element or null.
   */
  getSingleNode(selector) {
    this._validateRoot();
    return selectSingleFrom(this._root, this._getControllerSelector(selector));
  }

  /**
   * Create a selector for getting nodes inside the controller root.
   *
   * Will use a data attribute selector for if the raw selector starts with
   * a letter and isn't a common HTML element. Selecting via the data attribute
   * should generally be preferred since it promotes a consistent approach, but
   * it may not always be feasible, for instance when dealing with third party
   * output that may not be customizable enough.
   *
   * @param {string} rawSelector - Selector passed from a controller.
   * @return {string}
   */
  _getControllerSelector(rawSelector) {
    const commonElements = [
      'a',
      'div',
      'span',
      'ol',
      'ul',
      'li',
      'img',
      'audio',
      'video',
      'picture',
      'iframe',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'form',
      'fieldset',
      'label',
      'input',
      'button',
      'select',
      'option',
      'optgroup',
      'textarea',
      'meter',
      'progress',
    ];
    if (/[a-z]/.test(rawSelector[0]) && !commonElements.includes(rawSelector)) {
      return `[${NODE_NAME_ATTRIBUTE}~="${rawSelector}"]`;
    }
    return rawSelector;
  }

  /**
   * Throw an error if a root element isn't set.
   */
  _validateRoot() {
    if (!this._root) {
      throw new Error(
        `No root element set for controller '${
          this._controllerConstructor.name
        }', can't use element helpers.`,
      );
    }
  }
}
